import React, { ReactNode, RefObject } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  spacing,
  fontSize,
  fontWeight,
  borderRadius,
} from 'presentations/views/styles';
import {
  error,
  gray01,
  gray02,
  gray06,
  black,
  secondary,
} from 'presentations/views/styles/color';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Icon from 'presentations/views/components/atoms/Icon';
import Tag from 'presentations/views/components/atoms/Tag';
import FormLabel from 'presentations/views/components/molecules/FormLabel';
import ErrorText from 'presentations/views/components/atoms/ErrorText';
import { Description } from '../TextField';

interface Props {
  type?: string;
  label?: string;
  className?: string;
  placeholder?: string;
  value: any;
  inputValue?: string | null;
  defaultValue?: string | null;
  description?: string | ReactNode;
  isFullWidth?: boolean;
  isLoading?: boolean;
  width?: number;
  height?: number;
  isDisabled?: boolean;
  id?: string;
  name?: string;
  errors?: string[];
  errorTagIndexList?: number[];
  isSelect?: boolean;
  helperText?: string;
  rows?: number;
  maxRows?: number;
  isMultiline?: boolean;
  children?: ReactNode;
  onChange?: any;
  onSubmit?: any;
  onInputChange?: any;
  onDeleteTag?: any;
  isRequired?: boolean;
  isNotRequired?: boolean;
  onPaste?: React.ClipboardEventHandler<HTMLDivElement>;
}

export default function ChipTextField(props: Props) {
  const onKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      props.onSubmit();
    } else if (
      e.keyCode === 8 &&
      Array.isArray(props.value) &&
      props.value.length > 0 &&
      (props.inputValue === null || props.inputValue?.length === 0)
    ) {
      props.onDeleteTag(props.value.length - 1);
    }
  };

  const onBlur = (e: any) => {
    props.onSubmit();
  };

  const errors = props.errors ? props.errors : [];
  const isError = errors.length > 0;
  return (
    <>
      {props.label && (
        <FormLabel
          isRequired={props.isRequired}
          isNotRequired={props.isNotRequired}
        >
          {props.label}
        </FormLabel>
      )}
      {props.description && <Description>{props.description}</Description>}
      <Autocomplete
        multiple
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        clearOnBlur
        disableClearable={true}
        onInputChange={props.onInputChange}
        value={props.value}
        options={[]}
        renderTags={(rows: any[], getTagProps: any) => {
          return rows.map((row, index) => (
            <Tag
              variant="outlined"
              label={row}
              icon={
                props.errorTagIndexList?.includes(index) ? (
                  <ErrorIcon name="alert-circle-outline" />
                ) : undefined
              }
              deleteIcon={
                <Delete>
                  <Icon name="clear" />
                </Delete>
              }
              {...getTagProps({ index })}
              onDelete={() => {
                props.onDeleteTag(index);
              }}
            />
          ));
        }}
        onChange={props.onChange}
        getOptionLabel={(option) => option.title}
        freeSolo={true}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            type={props.type || 'text'}
            placeholder={props.placeholder}
            disabled={props.isDisabled || props.isLoading}
            InputLabelProps={{
              shrink: false,
            }}
            className={props.className}
            variant="outlined"
            defaultValue={props.defaultValue}
            fullWidth={props.isFullWidth}
            width={props.width}
            height={props.height}
            error={isError}
            name={props.name}
            rows={props.rows}
            maxRows={props.maxRows}
            multiline={props.isMultiline}
            onPaste={props.onPaste}
          >
            {props.children}
          </StyledTextField>
        )}
      />
      {props.helperText && (
        <HelperText dangerouslySetInnerHTML={{ __html: props.helperText }} />
      )}
      {isError &&
        errors.map((errorText, i) => (
          <ErrorText key={i}>{errorText}</ErrorText>
        ))}
    </>
  );
}

const StyledTextField = styled(TextField)<{
  width: number | undefined;
  height: number | undefined;
}>`
  width: ${(props) =>
    props.width &&
    css`
      ${props.width}px
    `};
  height: ${(props) =>
    props.height &&
    css`
      ${props.height}px
    `};
  .MuiInputBase-root {
    border-radius: ${borderRadius(6)};
    color: ${black};
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${gray02};
  }
`;
const HelperText = styled.div`
  font-size: ${fontSize(12)};
  margin-top: ${spacing(12)};
`;
const ErrorIcon = styled(Icon)`
  width: 16px !important;
  height: 16px !important;
  margin-left: ${spacing(3)};
  color: ${secondary};
`;
const Delete = styled.div`
  display: block;
`;
